import { CLIENT } from '@/generated/client';

// `useGrouping: 'always'`: the default ('auto') follows the runtime's ICU data, which
// groups 4-digit numbers on some platforms and not others (macOS `1210`, Linux `1.210`).
const number = (digits: number, locale: string = CLIENT.locale) =>
	new Intl.NumberFormat(locale, { maximumFractionDigits: digits, useGrouping: 'always' });

const dash = (v: number | null | undefined, f: (v: number) => string) => (v == null ? '-' : f(v));

/**
 * Locale and timezone of the output; both default to the client's (`CLIENT`). Tests pass them
 * explicitly so they hold in every client repo (ADR-007 "Formatting").
 */
export interface FormatOptions {
	locale?: string;
	timeZone?: string;
}

export const kw = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(0, o.locale).format(x)} kW`);
export const kwp = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(0, o.locale).format(x)} kWp`);
export const mwp = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(1, o.locale).format(x / 1000)} MWp`);
export const pct = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(0, o.locale).format(x)}%`);
export const kwh = (v: number | null | undefined) => dash(v, (x) => `${number(0).format(x)} kWh`);
export const eur = (v: number | null | undefined) =>
	dash(v, (x) =>
		new Intl.NumberFormat(CLIENT.locale, {
			style: 'currency',
			currency: 'EUR',
			useGrouping: 'always',
		}).format(x),
	);

/** Peak power: MWp from 1000 kWp up, kWp below. */
export const peakPower = (v: number | null | undefined, o: FormatOptions = {}) =>
	v != null && v >= 1000 ? mwp(v, o) : kwp(v, o);

/** Power: kW below 1000 kW, MW with 2 decimals from 1000 kW up. */
export const power = (v: number | null | undefined, o: FormatOptions = {}) =>
	v != null && v >= 1000 ? `${number(2, o.locale).format(v / 1000)} MW` : kw(v, o);

/** Energy: kWh below 1000 kWh, MWh (1 decimal) from 1000 kWh, GWh (2 decimals) from 10⁶ kWh. */
export const energy = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) =>
		x >= 1_000_000
			? `${number(2, o.locale).format(x / 1_000_000)} GWh`
			: x >= 1000
				? `${number(1, o.locale).format(x / 1000)} MWh`
				: `${number(1, o.locale).format(x)} kWh`,
	);

/** Percentage with one decimal (`4,6%`). */
export const pct1 = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(1, o.locale).format(x)}%`);

/** Integer count, grouped. */
export const int = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => number(0, o.locale).format(x));

/** Plain number, grouped, at most `digits` decimals (the table header carries the unit). */
export const num = (v: number | null | undefined, digits = 0, o: FormatOptions = {}) =>
	dash(v, (x) => number(digits, o.locale).format(x));

/**
 * Integer euro cents as `1.457,70 €`: always 2 decimals, grouped, the symbol after the amount.
 * A fixed rule, not the runtime's currency pattern.
 */
export const eurCents = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(
		v,
		(x) =>
			`${new Intl.NumberFormat(o.locale ?? CLIENT.locale, {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
				useGrouping: 'always',
			}).format(x / 100)} €`,
	);

/** Duration in hours, 1 decimal. */
export const hours = (v: number | null | undefined, o: FormatOptions = {}) =>
	dash(v, (x) => `${number(1, o.locale).format(x)} h`);

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// Only the numeric fields of an instant in the timezone; never a locale pattern (ADR-007).
function fields(instant: string, timeZone: string) {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		numberingSystem: 'latn',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	}).formatToParts(new Date(instant));
	const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
	return { y: get('year'), m: get('month'), d: get('day'), hh: get('hour'), mm: get('minute') };
}

/**
 * `dd/MM/yyyy`. A date-only ISO value (`YYYY-MM-DD`) is split, never parsed into a `Date`; an
 * instant is shown in the client's timezone.
 */
export const date = (v: string | null | undefined, o: FormatOptions = {}) => {
	if (v == null) return '-';
	const iso = ISO_DATE.exec(v);
	if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
	const f = fields(v, o.timeZone ?? CLIENT.timezone);
	return `${f.d}/${f.m}/${f.y}`;
};

// Year, month and day of a date-only value (split) or of an instant in the timezone.
function ymd(v: string, timeZone: string) {
	const iso = ISO_DATE.exec(v);
	const f = iso ? { y: iso[1], m: iso[2], d: iso[3] } : fields(v, timeZone);
	return { y: Number(f.y), m: Number(f.m), d: Number(f.d) };
}

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

/** `Mar 2023`: month names from a fixed Italian table (ADR-007). */
export const monthYear = (v: string | null | undefined, o: FormatOptions = {}) => {
	if (v == null) return '-';
	const { y, m } = ymd(v, o.timeZone ?? CLIENT.timezone);
	return `${MONTHS[m - 1]} ${y}`;
};

/**
 * Whole months from `v` to `today` (date-only values or instants, in the client's timezone).
 * `today` comes from the wiring: mappers never read the clock (ADR-007).
 */
export const monthsSince = (v: string | null | undefined, today: string, o: FormatOptions = {}) => {
	if (v == null) return '-';
	const tz = o.timeZone ?? CLIENT.timezone;
	const a = ymd(v, tz);
	const b = ymd(today, tz);
	return int((b.y - a.y) * 12 + (b.m - a.m) - (b.d < a.d ? 1 : 0), o);
};

/** `dd/MM/yyyy HH:mm` of an instant in the client's timezone. */
export const dateTime = (v: string | null | undefined, o: FormatOptions = {}) => {
	if (v == null) return '-';
	const f = fields(v, o.timeZone ?? CLIENT.timezone);
	return `${f.d}/${f.m}/${f.y} ${f.hh}:${f.mm}`;
};

const ROLE_LABELS: Record<string, string> = {
	admin: 'Amministratore',
	technician: 'Tecnico',
	customer: 'Cliente',
	subcontractor: 'Sub-appaltatore',
	platform_admin: 'Supporto InBlenda',
};

/** Role labels joined with ' · ' (a principal may hold several roles). */
export const roleLabel = (roles: readonly string[]) =>
	roles.map((r) => ROLE_LABELS[r] ?? r).join(' · ');

/** `HH:mm` of an instant in the client's timezone (chart labels). */
export const hhmm = (v: string, o: FormatOptions = {}) => {
	const f = fields(v, o.timeZone ?? CLIENT.timezone);
	return `${f.hh}:${f.mm}`;
};

/** `d/M` of an instant in the client's timezone (daily chart labels). */
export const dayMonth = (v: string, o: FormatOptions = {}) => {
	const { m, d } = ymd(v, o.timeZone ?? CLIENT.timezone);
	return `${d}/${m}`;
};

/** The investor report's number formats (MWh as integers, %, whole euros). */
export const investorFormat = {
	int: (v: number) => int(v),
	pct: (v: number) => `${num(v, 1)}%`,
	eur: (v: number) => `${int(v)} €`,
};

/** The commissioning I-V table: one decimal, signed percentages. */
export const commissioningFormat = {
	num1: (v: number) => num(v, 1),
	signedPct: (v: number) => `${v >= 0 ? '+' : ''}${num(v, 1)}%`,
};

/** The field views: integers, one decimal, coordinates with four. */
export const fieldFormat = {
	int: (v: number) => int(v),
	num1: (v: number) => num(v, 1),
	coord: (v: number) => num(v, 4),
};
